import { styled } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import { useTranslate } from '@repo/unicity'

export type BreadcrumbStep = {
    key: string
    label: string
}

export type CheckoutBreadcrumbProps = {
    steps: BreadcrumbStep[]
    activeIndex: number
    onStepClick?: (index: number, step: BreadcrumbStep) => void
    sx?: SxProps<Theme>
}

const CIRCLE_SIZE = 54
const DOT_SIZE_DESKTOP = 13
const DOT_SIZE_MOBILE = 7

export const CheckoutBreadcrumb = ({ steps, activeIndex, onStepClick, sx }: CheckoutBreadcrumbProps) => {
    const { translate } = useTranslate()

    return (
        <BreadcrumbContainer sx={sx} data-testid="checkout-breadcrumb-breadcrumb-container">
            {steps.map((step, index) => {
                const isActive = index === activeIndex
                const isPast = index < activeIndex
                const isClickable = !!(onStepClick && (isPast || isActive))
                const isLast = index === steps.length - 1

                return (
                    <BreadcrumbItemRow key={step.key} data-testid="checkout-breadcrumb-breadcrumb-item-row">
                        <BreadcrumbLinkItem
                            clickable={isClickable}
                            onClick={() => isClickable && onStepClick(index, step)}
                            data-testid="checkout-breadcrumb-breadcrumb-link-item">
                            <BreadcrumbCircle active={isActive} data-testid="checkout-breadcrumb-breadcrumb-circle" />
                            <BreadcrumbCaption active={isActive} data-testid="checkout-breadcrumb-breadcrumb-caption">
                                {translate(step.label)}
                            </BreadcrumbCaption>
                        </BreadcrumbLinkItem>
                        {!isLast && (
                            <BreadcrumbLeadLineWrapper data-testid="checkout-breadcrumb-breadcrumb-lead-line-wrapper">
                                <DashedLine data-testid="checkout-breadcrumb-dashed-line" />
                                <LeadDot data-testid="checkout-breadcrumb-lead-dot" />
                            </BreadcrumbLeadLineWrapper>
                        )}
                    </BreadcrumbItemRow>
                )
            })}
        </BreadcrumbContainer>
    )
}

const BreadcrumbContainer = styled(Container)`
    display: flex;
    justify-content: center;
    gap: ${CIRCLE_SIZE / 3}px;

    @media screen and (min-width: 600px) and (max-width: 829px) {
        gap: 10px;
    }
`

const BreadcrumbItemRow = styled(Box)`
    display: flex;
    gap: ${CIRCLE_SIZE / 3}px;

    @media screen and (min-width: 600px) and (max-width: 829px) {
        gap: 10px;
    }
`

const BreadcrumbLinkItem = styled(Box, {
    shouldForwardProp: p => p !== 'clickable'
})<{ clickable: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 60px;
    cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`

const BreadcrumbCircle = styled(Box, {
    shouldForwardProp: p => p !== 'active'
})<{ active: boolean }>`
    width: ${CIRCLE_SIZE}px;
    height: ${CIRCLE_SIZE}px;
    border-radius: 50%;
    background-color: ${({ active }) => (active ? '#083057' : '#A8C3DF')};
    transition: background-color 0.3s ease;
`

const BreadcrumbCaption = styled(Typography, {
    shouldForwardProp: p => p !== 'active'
})<{ active: boolean }>`
    display: flex;
    justify-content: center;
    margin-top: 7px;
    text-align: center;
    font-size: 14px;
    color: ${({ active }) => (active ? '#083057' : '#A8C3DF')};
    white-space: nowrap;

    @media screen and (max-width: 600px) {
        font-size: 12px;
    }
`

const BreadcrumbLeadLineWrapper = styled(Box)`
    display: flex;
    align-items: center;
    align-self: flex-start;
    padding-top: ${(CIRCLE_SIZE - DOT_SIZE_DESKTOP) / 2}px;
`

const DashedLine = styled(Box)`
    width: 176px;
    border-top: 1px dashed #a8c3df;

    /* Mobile: drop the dashed connector — show only the lead dot between steps. */
    @media screen and (max-width: 599px) {
        display: none;
    }

    @media screen and (min-width: 600px) and (max-width: 1199px) {
        width: 120px;
    }
`

const LeadDot = styled(Box)`
    width: ${DOT_SIZE_DESKTOP}px;
    height: ${DOT_SIZE_DESKTOP}px;
    border-radius: 50%;
    background-color: #a8c3df;
    flex-shrink: 0;

    @media screen and (max-width: 599px) {
        width: ${DOT_SIZE_MOBILE}px;
        height: ${DOT_SIZE_MOBILE}px;
    }
`
